import React, { useState } from "react";
import {
  VakLastTestResponse,
  VakTestData,
  VakType,
  VakCategoryInfo,
} from "../../typings/vak";
import RadarChart from "../RadarChart";
// import RiasecPdfDownload from "./RiasecPdfDownload";
import { vakService } from "../../services/vak.service";
import Button from "../UI/Button";
import { getAnswerOptionLabels, VAK_CATEGORIES } from "../../utils/vak";
import VakAiRecommendation from "./VakAiRecommendation";
import { ChevronDown, ChevronUp, Clock, RefreshCcw, CheckCircle2, AlertCircle, Loader2, Hourglass } from "lucide-react";
import VakPdfDownload from "./VakPdfDownload";
import { Card, CardContent, CardHeader } from "../UI/Card";

interface VakTestResultProps {
  testResult: VakLastTestResponse;
  onBackToHome: () => void;
}

const VakTestResult: React.FC<VakTestResultProps> = ({
  testResult: initialTestResult,
  onBackToHome,
}) => {
  const [testResult, setTestResult] =
    useState<VakLastTestResponse>(initialTestResult);
  const [expandedCategories, setExpandedCategories] = useState<{
    [key: string]: boolean;
  }>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [expandDescAI, setExpandDescAI] = useState<boolean>(false);

  const test = testResult.data.test;

  React.useEffect(() => {
    let intervalId: any;
    let startTime = Date.now();

    if (!test.ai_generated) {
      intervalId = setInterval(async () => {
        const elapsedTime = (Date.now() - startTime) / 1000;
        if (elapsedTime >= 30) {
          clearInterval(intervalId);
          return;
        }

        try {
          const response = await vakService.getTestById(test.id);
          if (response.data.test.ai_generated) {
            setTestResult(response);
            clearInterval(intervalId);
          }
        } catch (err) {
          console.error("Auto refresh error:", err);
        }
      }, 5000); // Check every 5 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [test.id, test.ai_generated]);

  const getRadarData = (testData: VakTestData) => {
    return {
      labels: ["Visual", "Auditory", "Kinesthetic"],
      datasets: [
        {
          label: "SLP Score",
          data: [testData.v, testData.a, testData.k],
          backgroundColor: "rgba(54, 162, 235, 0.2)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 2,
          pointBackgroundColor: "rgba(54, 162, 235, 1)",
        },
      ],
    };
  };

  const toggleExpanded = (categoryType: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryType]: !prev[categoryType],
    }));
  };

  const getTop3Categories = () => {
    return VAK_CATEGORIES.map((category) => ({
      ...category,
      score: testResult.data.test[
        category.type.toLowerCase() as keyof VakTestData
      ] as number,
    }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  };

  const handleRefreshTestData = async () => {
    setIsRefreshing(true);
    setAiError(null);

    try {
      const response = await vakService.getTestById(
        testResult.data.test.id
      );
      setTestResult(response);
    } catch (error) {
      setAiError("Gagal memuat ulang data. Silakan coba lagi.");
      console.error("Error refreshing test data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="riasec-container">
      <div className="w-full">
        <div className="flex flex-wrap">
          <div className="w-full p-4">
            <Card className="mt-4 mb-4">
              <CardHeader className="border-b border-indigo-50 bg-indigo-50/30">
                <div className="flex justify-between items-center">
                  <h5 className="text-lg font-bold text-indigo-900">
                    Hasil Terakhir
                  </h5>
                  <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full font-medium">
                    {new Date(
                      testResult.data.test.created_at
                    ).toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div>
                  <div className="flex flex-col md:flex-row md:flex-wrap">
                    <div className="md:w-full mb-3">
                      <h6 className="text-center font-semibold mb-3">Grafik</h6>
                      <div style={{ height: "250px" }}>
                        <RadarChart data={getRadarData(testResult.data.test)} />
                      </div>
                    </div>

                    <div className="md:w-full">
                      <div className="riasec-scores">
                        {getTop3Categories().map((category, index) => {
                          const isExpanded = expandedCategories[category.type];

                          return (
                            <div
                              key={category.type}
                              className="mb-3 pb-2 border-b"
                            >
                              <div className="flex justify-between items-center mb-1">
                                <span style={{ color: category.color }}>
                                  <strong>
                                    #{index + 1} {category.title}
                                  </strong>
                                </span>
                                <span
                                  className="inline-block px-2 py-1 text-xs font-medium rounded"
                                  style={{
                                    backgroundColor: category.color,
                                    color: "#fff",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {category.score}
                                </span>
                              </div>
                              <small className="text-gray-500 block mb-2">
                                {category.description}
                              </small>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* AI Recommendation Section */}
            <div className="mt-4 mb-4">
              {!test.ai_generated ? (
                <div
                  className="p-4 rounded border bg-amber-50 border-amber-200 text-amber-800 flex flex-col md:flex-row justify-between items-center gap-4"
                  role="alert"
                >
                  <div className="flex gap-4 items-start">
                    <div className="bg-amber-100 p-3 rounded-full">
                      <Hourglass className={`w-6 h-6 text-amber-600 animate-pulse`} />
                    </div>
                    <div>
                      <h4 className="font-bold text-amber-900 mb-0">
                        Analisis sedang dalam proses
                      </h4>
                      <p className="mb-0 mt-1 text-sm">
                        Laporan mendalam sedang disiapkan oleh AI. Halaman akan diperbarui secara otomatis.
                      </p>
                    </div>
                  </div>
                  <Button
                    warning
                    className="font-bold whitespace-nowrap"
                    onClick={handleRefreshTestData}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Lihat Hasil"
                    )}
                  </Button>
                </div>
              ) : (
                <VakAiRecommendation testData={test} />
              )}
              {aiError && (
                <div
                  className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mt-2"
                  role="alert"
                >
                  {aiError}
                </div>
              )}
            </div>
            <div className="bg-white rounded-lg shadow border riasec-card">
              <div className="p-4">
                <div className="mt-4">
                  <h6>Detail Jawaban</h6>
                  <div
                    className="answers-detail"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {testResult.data.test_questions.map((item, index) => (
                      <div
                        key={item.id}
                        className="answer-item mb-2 p-2 border rounded"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <span>
                            <strong>{index + 1}:</strong> {item.question}
                          </span>
                          <span
                            className={`font-bold ${
                              item.answer ? "text-primary" : "text-foreground"
                            }`}
                          >
                            {getAnswerOptionLabels(item.answer)?.label}
                          </span>
                        </div>
                        <small className="text-gray-500">
                          Kategori: {item.type}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col mt-3">
                  <Button outline className="mb-2" onClick={onBackToHome}>
                    Kembali ke Halaman Utama
                  </Button>
                  <VakPdfDownload
                    testId={testResult.data.test.id}
                    testData={testResult.data.test}
                    testDate={testResult.data.test.created_at}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VakTestResult;
